import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Grid,
    CircularProgress,
    Button,
    alpha,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { GlassCard, StyledInput, Label } from '../StyledComponents/StyledComponent';
import { getEmployeeByToken, submitJoiningForm } from '../../api';
import { useAlert } from '../../context/AlertContext';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Native select styled to match StyledInput (InputBase has no select variant).
const StyledSelect = styled('select')(({ theme }) => ({
    width: '100%',
    fontFamily: "'Outfit', 'Inter', 'Poppins', sans-serif",
    fontSize: '0.875rem',
    fontWeight: 400,
    padding: '8px 12px',
    height: 40,
    borderRadius: 10,
    color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#0f172a',
    background: theme.palette.mode === 'dark' ? alpha('#0f172a', 0.4) : alpha('#fff', 0.6),
    backdropFilter: 'blur(8px)',
    border: `1px solid ${alpha('#e2e8f0', 0.8)}`,
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.2s ease',
    '&:hover': { borderColor: '#3DAC5A' },
    '&:focus': { borderColor: '#3DAC5A', boxShadow: `0 0 0 4px ${alpha('#3DAC5A', 0.15)}` },
    [theme.breakpoints.down('sm')]: { height: 44, minHeight: 44 },
}));

const SectionTitle = styled(Typography)(() => ({
    fontSize: '1.15rem',
    fontWeight: 800,
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px'
}));

// Editable field groups — mirrors the legacy Employee Joining Form.
const FIELD_SECTIONS = [
    {
        title: 'Personal Information',
        icon: <PersonIcon sx={{ color: '#3DAC5A' }} />,
        fields: [
            { key: 'employee_no', label: 'Employee No', required: true },
            { key: 'name', label: 'Full Name', required: true },
            { key: 'gender', label: 'Gender', type: 'select', options: [{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }] },
            { key: 'date_of_birth', label: 'Date of Birth', type: 'date' },
            { key: 'email', label: 'Email', type: 'email' },
            { key: 'mobile_number', label: 'Mobile Number' },
            { key: 'nationality', label: 'Nationality' },
        ],
    },
    {
        title: 'Job Details',
        icon: <WorkIcon sx={{ color: '#3DAC5A' }} />,
        fields: [
            { key: 'date_of_joining', label: 'Date of Joining', type: 'date' },
            { key: 'designation', label: 'Designation' },
            { key: 'department', label: 'Department' },
            { key: 'employment_status', label: 'Employment Status', type: 'select', options: [{ value: 'Probation', label: 'Probation' }, { value: 'Confirmed', label: 'Confirmed' }, { value: 'Active', label: 'Active' }] },
            { key: 'reporting_manager', label: 'Reporting Manager' },
            { key: 'team_head', label: 'Team Head', type: 'select', optionsFrom: 'teamHeads' },
            { key: 'branch_name', label: 'Branch Name', type: 'select', optionsFrom: 'branches' },
            { key: 'division', label: 'Division' },
            { key: 'location', label: 'Location', type: 'select', optionsFrom: 'locations' },
            { key: 'attendance_schema', label: 'Attendance Schema' },
            { key: 'level_cost_center', label: 'Level / Cost Center' },
        ],
    },
    {
        title: 'Bank & Statutory',
        icon: <AccountBalanceIcon sx={{ color: '#3DAC5A' }} />,
        fields: [
            { key: 'bank_account_no', label: 'Bank Account Number' },
            { key: 'ifsc_code', label: 'IFSC Code' },
            { key: 'aadhaar_number', label: 'Aadhaar Number' },
            { key: 'pan_no', label: 'PAN Number' },
            { key: 'uan_no', label: 'UAN Number' },
        ],
    },
];

const FIELD_KEYS = FIELD_SECTIONS.flatMap((s) => s.fields.map((f) => f.key));

const EmployeeOnboardingForm = () => {
    // routes/index.js renders this directly (no <Route> param), so useParams is
    // empty — fall back to the token in the URL path: /employee/form/<token>/
    const params = useParams();
    const token = params.token || window.location.pathname.split('/').filter(Boolean).pop();

    const [loaded, setLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [form, setForm] = useState({});
    const [teamHeads, setTeamHeads] = useState([]);
    const [branches, setBranches] = useState([]);
    const [locations, setLocations] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const { showSuccess, showError } = useAlert();

    const hydrate = (data) => {
        const emp = data.employee || {};
        const next = {};
        FIELD_KEYS.forEach((k) => { next[k] = emp[k] ?? ''; });
        // Normalise gender case so a stored 'Male'/'Female' matches the lowercase
        // select options (and round-trips as a valid choice on submit).
        if (next.gender) next.gender = String(next.gender).toLowerCase();
        setForm(next);
        setTeamHeads(data.team_heads || []);
        setBranches(data.branches || []);
        setLocations(data.locations || []);
    };

    useEffect(() => {
        const fetchEmployee = async () => {
            try {
                const data = await getEmployeeByToken(token);
                hydrate(data);
                setLoaded(true);
            } catch (err) {
                console.error('Error fetching employee:', err);
                showError('Invalid or expired onboarding link.');
            } finally {
                setIsLoading(false);
            }
        };
        if (token) fetchEmployee();
    }, [token, showError]);

    const handleChange = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleSubmit = async () => {
        if (!form.employee_no || !form.name) {
            showError('Employee No and Full Name are required.');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {};
            FIELD_KEYS.forEach((k) => {
                let v = form[k];
                if ((k === 'date_of_joining' || k === 'date_of_birth') && !v) v = null;
                payload[k] = v;
            });
            await submitJoiningForm(token, payload);
            setSubmitted(true);
            showSuccess('Joining form submitted successfully!');
        } catch (err) {
            console.error('Submit failed:', err);
            const detail = err?.response?.data;
            const firstErr = detail && typeof detail === 'object'
                ? Object.entries(detail).map(([f, m]) => `${f}: ${Array.isArray(m) ? m[0] : m}`)[0]
                : null;
            showError(firstErr || 'Could not submit the form. Please review your details.');
        } finally {
            setSubmitting(false);
        }
    };

    const OPTIONS_FROM_SOURCES = { teamHeads, branches, locations };

    const renderField = (f) => {
        const value = form[f.key] ?? '';
        if (f.type === 'select') {
            const options = f.optionsFrom
                ? (OPTIONS_FROM_SOURCES[f.optionsFrom] || []).map((n) => ({ value: n, label: n }))
                : (f.options || []);
            // Keep a legacy/pre-filled value that isn't among the known options
            // visible (so the field shows the current DB value rather than
            // silently blanking it) — the backend leaves such values untouched.
            if (value && !options.some((o) => o.value === value)) {
                options.unshift({ value, label: value });
            }
            return (
                <StyledSelect value={value} onChange={(e) => handleChange(f.key, e.target.value)}>
                    <option value="">Select</option>
                    {options.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </StyledSelect>
            );
        }
        return (
            <StyledInput
                value={value}
                type={f.type || 'text'}
                onChange={(e) => handleChange(f.key, e.target.value)}
            />
        );
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f8fafc' }}>
                <CircularProgress color="success" />
            </Box>
        );
    }

    if (!loaded) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f8fafc', p: 3 }}>
                <GlassCard sx={{ p: 6, textAlign: 'center', maxWidth: 500 }}>
                    <Typography variant="h5" color="error" gutterBottom sx={{ fontWeight: 800 }}>
                        Link Invalid
                    </Typography>
                    <Typography color="text.secondary">
                        The onboarding link you followed is invalid or has expired. Please contact HR for a new link.
                    </Typography>
                </GlassCard>
            </Box>
        );
    }

    if (submitted) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)', p: 3 }}>
                <GlassCard sx={{ p: 6, textAlign: 'center', maxWidth: 560 }}>
                    <CheckCircleIcon sx={{ fontSize: 72, color: '#3DAC5A', mb: 2 }} />
                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b', mb: 1 }}>
                        Thank you, {form.name}! 🎉
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Your joining details have been submitted. Our HR team will review them and get in
                        touch with the next steps. You can revisit this link any time to upload more documents.
                    </Typography>
                    <Button variant="outlined" onClick={() => setSubmitted(false)}
                        sx={{ textTransform: 'none', borderRadius: '10px', borderColor: '#3DAC5A', color: '#3DAC5A' }}>
                        Back to form
                    </Button>
                </GlassCard>
            </Box>
        );
    }

    return (
        <Box sx={{
            py: 6,
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)',
            backgroundImage: `radial-gradient(at 0% 0%, rgba(61, 172, 90, 0.05) 0px, transparent 50%), radial-gradient(at 100% 100%, rgba(61, 172, 90, 0.03) 0px, transparent 50%)`
        }}>
            <Container maxWidth="lg">
                <Box sx={{ mb: 5, textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 900, color: '#1e293b', mb: 1, letterSpacing: '-0.02em' }}>
                        Employee Joining Form
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Hi {form.name || 'there'}, complete your details and upload the required documents.
                    </Typography>
                </Box>

                <Grid container spacing={4}>
                    {FIELD_SECTIONS.map((section) => (
                        <Grid item xs={12} md={section.title === 'Bank & Statutory' ? 12 : 6} key={section.title}>
                            <GlassCard sx={{ p: 4, height: '100%' }}>
                                <SectionTitle>
                                    {section.icon}
                                    {section.title}
                                </SectionTitle>
                                <Grid container spacing={3}>
                                    {section.fields.map((f) => (
                                        <Grid item xs={12} sm={6} key={f.key}>
                                            <Label>{f.label}{f.required ? ' *' : ''}</Label>
                                            {renderField(f)}
                                        </Grid>
                                    ))}
                                </Grid>
                            </GlassCard>
                        </Grid>
                    ))}

                    <Grid item xs={12}>
                        <GlassCard sx={{ p: 4 }}>
                            <SectionTitle>
                                <CloudUploadIcon sx={{ color: '#3DAC5A' }} />
                                Secure Document Uploads
                            </SectionTitle>
                            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: alpha('#3DAC5A', 0.08), border: `1px solid ${alpha('#3DAC5A', 0.2)}` }}>
                                <Typography variant="body2" sx={{ color: '#1e5930', fontWeight: 700, mb: 0.75 }}>
                                    Use the separate Document Upload link in your onboarding invitation.
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    That secure link validates each PDF, JPG, JPEG, or PNG file, keeps it private during malware scanning, and releases it to HR only after a clean result.
                                </Typography>
                            </Box>
                        </GlassCard>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 5, display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '12px',
                            px: 6,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #3DAC5A 0%, #2d8a47 100%)',
                            boxShadow: '0 8px 24px rgba(61, 172, 90, 0.3)',
                            width: { xs: '100%', sm: 'auto' },
                            minHeight: 44,
                            '&:hover': { background: 'linear-gradient(135deg, #2d8a47 0%, #237038 100%)' }
                        }}
                    >
                        {submitting ? 'Submitting…' : 'Submit Joining Form'}
                    </Button>
                </Box>

                <Box sx={{ mt: 5, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        © 2026 Aditya Trading Solutions. All rights reserved.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default EmployeeOnboardingForm;
